/**
 * Eligibility config validation schemas.
 * Defines rules for filtering products based on user inputs and constraints.
 *
 * Supports recursive condition types:
 * - ComparisonCondition: field/operator/value comparisons
 * - AndCondition: logical AND of multiple conditions
 * - OrCondition: logical OR of multiple conditions
 * - NotCondition: logical NOT of a single condition
 */
import { z } from "zod";
import { ComparisonOperatorSchema } from "./attribute";
// ============================================================================
// Comparison Condition (base leaf condition)
// ============================================================================
/**
 * Comparison condition - the base leaf condition type.
 * Compares a field value against an expected value using an operator.
 *
 * Value can be:
 * - Static: specified directly in `value` field
 * - Dynamic: specified as a reference path in `valueRef` field
 */
export const ComparisonConditionSchema = z
    .object({
    /** Field path to evaluate (e.g., "product.price", "user.age") */
    field: z.string().min(1),
    /** Comparison operator to use */
    operator: ComparisonOperatorSchema,
    /** Static value to compare against (mutually exclusive with valueRef) */
    value: z.unknown().optional(),
    /** Dynamic value reference path (e.g., "user.maxBudget") */
    valueRef: z.string().min(1).optional(),
})
    .strict()
    .refine((data) => data.value !== undefined || data.valueRef !== undefined, {
    message: "Either value or valueRef must be provided",
});
/**
 * Base schemas for And/Or/Not conditions (without recursive references).
 * These are used internally to build the recursive schemas.
 */
const BaseAndConditionSchema = z
    .object({
    type: z.literal("and"),
    conditions: z.array(z.lazy(() => RuleConditionSchema)).min(1),
})
    .strict();
const BaseOrConditionSchema = z
    .object({
    type: z.literal("or"),
    conditions: z.array(z.lazy(() => RuleConditionSchema)).min(1),
})
    .strict();
const BaseNotConditionSchema = z
    .object({
    type: z.literal("not"),
    condition: z.lazy(() => RuleConditionSchema),
})
    .strict();
/**
 * Schema for And condition with recursive validation.
 */
export const AndConditionSchema = BaseAndConditionSchema;
/**
 * Schema for Or condition with recursive validation.
 */
export const OrConditionSchema = BaseOrConditionSchema;
/**
 * Schema for Not condition with recursive validation.
 */
export const NotConditionSchema = BaseNotConditionSchema;
/**
 * RuleConditionSchema - validates any condition type.
 *
 * Determines type by checking:
 * - If `type` is "and" -> AndCondition
 * - If `type` is "or" -> OrCondition
 * - If `type` is "not" -> NotCondition
 * - Otherwise -> ComparisonCondition (default)
 */
export const RuleConditionSchema = z.union([
    BaseAndConditionSchema,
    BaseOrConditionSchema,
    BaseNotConditionSchema,
    ComparisonConditionSchema,
]);
// ============================================================================
// Legacy EligibilityCondition (backward compatibility)
// ============================================================================
/**
 * EligibilityConditionSchema - backward compatible simple condition.
 * For new code, prefer using RuleConditionSchema which supports nesting.
 *
 * @deprecated Use RuleConditionSchema for new code
 */
export const EligibilityConditionSchema = z
    .object({
    field: z.string().min(1),
    operator: ComparisonOperatorSchema,
    value: z.unknown(),
})
    .strict();
// ============================================================================
// Eligibility Rules
// ============================================================================
/**
 * Eligibility rule - a named set of conditions with a reason code.
 *
 * Rules contain an array of conditions that are implicitly AND-ed together.
 * Each condition can be a simple comparison or a nested And/Or/Not condition.
 *
 * When a rule fails, the reasonCode and reasonMessage explain why the
 * product was excluded.
 */
export const EligibilityRuleSchema = z
    .object({
    /** Unique rule identifier */
    id: z.string().min(1),
    /** Human-readable rule name */
    name: z.string().min(1).max(100),
    /** Optional description of the rule's purpose */
    description: z.string().max(500).optional(),
    /**
     * Conditions to evaluate (implicitly AND-ed).
     * Supports nested And/Or/Not conditions via RuleConditionSchema.
     */
    conditions: z.array(RuleConditionSchema).min(1),
    /** Reason code for exclusion (SCREAMING_SNAKE_CASE) */
    reasonCode: z
        .string()
        .min(1)
        .regex(/^[A-Z][A-Z0-9_]*$/, "Reason code must be SCREAMING_SNAKE_CASE"),
    /** Human-readable exclusion message (supports template interpolation) */
    reasonMessage: z.string().min(1).max(255),
    /** Rule evaluation priority (lower = evaluated first) */
    priority: z.number().int().min(0).optional(),
})
    .strict();
// ============================================================================
// Eligibility Rule Groups
// ============================================================================
/**
 * Rule reference - can be a rule ID (string) or an inline rule object.
 * Allows rule groups to reference rules by ID for DRY configs or inline rules.
 */
export const RuleReferenceSchema = z.union([
    z.string().min(1), // Rule ID reference
    EligibilityRuleSchema, // Inline rule object
]);
/**
 * Eligibility rule group - groups rules with AND/OR logic.
 *
 * Rule groups allow combining multiple rules:
 * - AND: All rules must pass for the group to pass
 * - OR: At least one rule must pass for the group to pass
 */
export const EligibilityRuleGroupSchema = z
    .object({
    /** Unique group identifier */
    id: z.string().min(1),
    /** Human-readable group name */
    name: z.string().min(1).max(100),
    /** Optional description of the group's purpose */
    description: z.string().max(500).optional(),
    /** Rules in the group (can be rule IDs or inline rule objects) */
    rules: z.array(RuleReferenceSchema).min(1),
    /** Logical operator to combine rules */
    operator: z.enum(["AND", "OR"]),
})
    .strict();
// ============================================================================
// Eligibility Configuration
// ============================================================================
/** Complete eligibility configuration */
export const EligibilityConfigSchema = z
    .object({
    verticalKey: z.string().min(1),
    version: z.number().int().min(1),
    rules: z.array(EligibilityRuleSchema),
    ruleGroups: z.array(EligibilityRuleGroupSchema).optional(),
    defaultExclusionReason: z.string().max(255).optional(),
})
    .strict()
    .refine((data) => {
    // At least one rule or rule group must be defined
    return data.rules.length > 0 || (data.ruleGroups && data.ruleGroups.length > 0);
}, {
    message: "At least one rule or rule group must be defined",
});
// ============================================================================
// Eligibility Results
// ============================================================================
/** Failed condition detail */
export const FailedConditionSchema = z
    .object({
    field: z.string(),
    operator: ComparisonOperatorSchema,
    expectedValue: z.unknown(),
    actualValue: z.unknown(),
})
    .strict();
/** Exclusion reason with explanation */
export const ExclusionReasonSchema = z
    .object({
    ruleId: z.string(),
    ruleName: z.string(),
    reasonCode: z.string(),
    reasonMessage: z.string(),
    failedConditions: z.array(FailedConditionSchema),
})
    .strict();
/** Eligibility check result */
export const EligibilityResultSchema = z
    .object({
    productId: z.string(),
    eligible: z.boolean(),
    exclusionReasons: z.array(ExclusionReasonSchema),
})
    .strict();
